import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wallet, Clock, TrendingUp, AlertCircle } from "lucide-react";
import { useWallet } from "@/hooks/use-wallet";
import { isIndiaCard, formatLandsBy, useCanManageBilling } from "@/hooks/use-workspace-billing";
import { formatMinutes, formatMoney } from "@/lib/money";
import { TopUpDialog } from "./TopUpDialog";

export function WalletBalance() {
  const { walletBalance, freeMinutes, freeMinutesValue, totalAvailable, profile } = useWallet();
  const canManage = useCanManageBilling();
  // One state so a preset from "Add funds" is dropped when the dialog closes.
  const [topUp, setTopUp] = useState<{ open: boolean; preset?: number }>({ open: false });

  const isIndia = isIndiaCard(profile);
  const pending = profile?.auto_reload_pending
    ? {
        amount: formatMoney(profile.auto_reload_pending_amount_credits ?? profile.auto_reload_amount_credits),
        landsBy: formatLandsBy(profile.auto_reload_pending_completes_at, isIndia),
      }
    : null;
  // India cards get a low-balance nudge (their reloads land ~a day late, if at all).
  const lowBalance = isIndia && !pending && !!profile && walletBalance < profile.auto_reload_threshold_credits;

  return (
    <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary/10 via-background to-primary/5 shadow-lg">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

      <CardHeader className="relative pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-inner">
              <Wallet className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold">Wallet Balance</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your available balance for call usage
              </p>
            </div>
          </div>
          <TopUpDialog
            open={topUp.open}
            onOpenChange={(open) => setTopUp({ open })}
            presetAmount={topUp.preset}
          />
        </div>
      </CardHeader>
      <CardContent className="relative pt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Wallet Credits */}
          <div className="p-5 rounded-2xl bg-background/60 backdrop-blur-sm border border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">Wallet Credits</p>
            </div>
            <p className="text-4xl font-bold tracking-tight">{formatMoney(walletBalance)}</p>
          </div>

          {/* Free Minutes */}
          <div className="p-5 rounded-2xl bg-background/60 backdrop-blur-sm border border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">Free Minutes</p>
            </div>
            <p className="text-4xl font-bold tracking-tight">{formatMinutes(freeMinutes)} <span className="text-lg font-normal text-muted-foreground">min</span></p>
            <p className="text-xs text-muted-foreground mt-1">≈ {formatMoney(freeMinutesValue)}</p>
          </div>

          {/* Total Available */}
          <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-primary">Total Available</p>
            </div>
            <p className="text-4xl font-bold tracking-tight text-primary">{formatMoney(totalAvailable)}</p>
          </div>
        </div>

        {pending && (
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4 flex-shrink-0" />
            Automatic reload of {pending.amount}: your bank completes it after {pending.landsBy}
          </p>
        )}

        {lowBalance && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/50">
            <p className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Your balance is below your {formatMoney(profile.auto_reload_threshold_credits)} threshold.
            </p>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTopUp({ open: true, preset: profile.auto_reload_amount_credits })}
              >
                Add funds
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
